
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import List from '@/common/components/UI/List/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'

// module components
import SettingsListItem from '@/modules/SettingsModule/components/ContractTypeSettingsListItem/index.vue'
import SettingsContractTypesActions from '@/modules/SettingsModule/components/ContractTypesActions/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import {
    SettingsContractType,
    CreateSettingsContractTypePayload,
    UpdateSettingsContractTypePayload,
} from '@/new-services/types/settings-contract-types'

// service
import settingsContractTypesAPI from '@/new-services/settingsContractTypes.service'

// composable
import useActions from '@/common/composable/useActions'
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'failed-fetch': FailedFetch,
        list: List,
        'the-button': TheButton,
        'settings-list-item': SettingsListItem,
        'settings-contract-types-actions': SettingsContractTypesActions,
        TheInput,
    },
    setup() {
        const { havePermissions } = useAccount()
        const notifications = useNotifications()
        const state = reactive(new DataState<SettingsContractType>({}))
        const filters = reactive({
            name: null as string | null,
        })

        const loadSettings = async (page: number) => {
            state.pagination.current_page = page

            try {
                state.is_loading = true

                const response = await settingsContractTypesAPI.getSettingsContractTypes({
                    page,
                    name: filters.name,
                })
                state.list = response.data.data
                state.pagination.total_pages = response.data.meta.last_page

                state.is_failed = false
                state.failure.error_state.is_cancel = false
            } catch (error) {
                state.failure.catchError(error)

                if (!state.failure.error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadSettings(1)
        })

        const { actions_state, startCreate, startUpdate } = useActions<SettingsContractType>()

        const createContractType = async (payload: CreateSettingsContractTypePayload) => {
            try {
                actions_state.is_actions_loading = true

                await settingsContractTypesAPI.createSettingsContractType(payload)
                await loadSettings(state.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ title: 'Ошибка при добавлении типа договора', error })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateContractType = async ({
            id,
            payload,
        }: {
            id: number
            payload: UpdateSettingsContractTypePayload
        }) => {
            try {
                actions_state.is_actions_loading = true

                await settingsContractTypesAPI.updateSettingsContractType(id, payload)
                await loadSettings(state.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ title: 'Ошибка при сохранении типа договора', error })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const reset = () => {
            filters.name = null
            loadSettings(1)
        }

        return {
            havePermissions,
            state,
            filters,
            loadSettings,
            actions_state,
            startCreate,
            startUpdate,
            createContractType,
            updateContractType,
            reset,
        }
    },
})
